import React from 'react';
import { ArrowLeft } from 'lucide-react';

interface FixListBarProps {
  propertyAddress: string;
  /** 1=Pick, 2=Actions, 3=Review. Confirm has no stepper (pass undefined). */
  step?: 1 | 2 | 3;
  stepLabel?: string;
  /** Custom back-button label and handler. Defaults to "Back to report". */
  onBack?: () => void;
  backLabel?: string;
  /** Optional primary action shown on the right of the bar. */
  primary?: React.ReactNode;
}

const TASK_STEPS = 3;

// Top app bar shared across the four task screens. Mobile collapses to:
// arrow-only back, address (no eyebrow), no stepper, compact primary —
// the in-page H2 carries the step context.
export const FixListBar: React.FC<FixListBarProps> = ({
  propertyAddress, step, stepLabel, onBack, backLabel = 'Back to report', primary,
}) => (
  <div className="sticky top-0 z-30 backdrop-blur bg-surface-0/85 border-b border-border-subtle">
    <div className="max-w-[860px] mx-auto px-3 sm:px-8 py-2.5 sm:py-3 flex items-center gap-2 sm:gap-4">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md text-body-sm text-ink-subtle hover:text-ink hover:bg-surface-2 transition-colors duration-180 shrink-0"
          aria-label={backLabel}
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          <span className="hidden sm:inline">{backLabel}</span>
        </button>
      )}
      <div className="flex-1 min-w-0">
        <div className="hidden sm:block text-overline uppercase text-brand">Fix list</div>
        <div className="font-display text-body-md sm:text-heading-md text-ink truncate">{propertyAddress}</div>
      </div>
      {step && (
        <span className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-surface-2 border border-border-subtle text-caption text-ink-muted shrink-0">
          <span className="inline-flex gap-1">
            {Array.from({ length: TASK_STEPS }, (_, i) => {
              const idx = i + 1;
              const cls =
                idx < step ? 'bg-brand'
                : idx === step ? 'bg-brand ring-2 ring-brand/25'
                : 'bg-ink-faint/40';
              return <span key={i} className={`h-1.5 w-1.5 rounded-full ${cls}`} />;
            })}
          </span>
          <span className="font-mono tabular-nums text-caption text-ink-muted">
            Step {step} of {TASK_STEPS}
          </span>
          {stepLabel && <span className="text-ink-subtle">· {stepLabel}</span>}
        </span>
      )}
      {primary && <div className="shrink-0">{primary}</div>}
    </div>
  </div>
);
